import {createContext, useCallback, useRef} from "react";
import classes from "./form-elements.module.css";

export type RadioContextType = {
    selectedOption: string | undefined;
    toggle: (value: string) => void;
};

export const RadioContext = createContext<RadioContextType>({
    selectedOption: undefined,
    toggle: () => {},
});

interface RadioGroupProps {
    children: React.ReactNode;
    id?: string;
    onChange: (value: string) => void;
    options: string[];
    value: string | undefined;
    className?: string;
}

export const RadioGroup = ({
    children,
    id,
    onChange,
    options,
    value,
    className,
}: RadioGroupProps) => {
    const ulRef = useRef<HTMLUListElement>(null);

    const toggle = useCallback(
        (val: string) => {
            if (val !== value) {
                onChange(val);
            }
        },
        [onChange, value]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!options.length) return;

            const currentIndex = value ? options.indexOf(value) : -1;

            switch (e.key) {
                case "ArrowDown":
                case "ArrowRight": {
                    e.preventDefault();
                    const next =
                        currentIndex === -1
                            ? 0
                            : (currentIndex + 1) % options.length;
                    onChange(options[next]);
                    break;
                }
                case "ArrowUp":
                case "ArrowLeft": {
                    e.preventDefault();
                    const prev =
                        currentIndex === -1
                            ? options.length - 1
                            : (currentIndex - 1 + options.length) %
                              options.length;
                    onChange(options[prev]);
                    break;
                }
                case "Enter":
                case " ": {
                    e.preventDefault();
                    if (value) onChange(value);
                    break;
                }
            }
        },
        [onChange, options, value]
    );

    return (
        <RadioContext.Provider value={{selectedOption: value, toggle}}>
            <ul
                ref={ulRef}
                id={id}
                role="radiogroup"
                tabIndex={0}
                className={`${classes.radioGroup}${className ? ` ${className}` : ""}`}
                onKeyDown={handleKeyDown}
            >
                {children}
            </ul>
        </RadioContext.Provider>
    );
};
